import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, CreditCard, Smartphone, DollarSign, Apple, Loader2, Info, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { drawerOverlay, modalEnter } from "@/lib/motion-presets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_METHODS = [
  {
    id: "stripe_elements",
    label: "Card or Cash App Pay",
    sub: "Stripe · Apple Pay & Google Pay supported",
    icon: <CreditCard size={18} />,
    badge: "Recommended",
  },
  {
    id: "zelle",
    label: "Zelle",
    sub: "Manual — send & we confirm",
    icon: <Smartphone size={18} />,
  },
  {
    id: "cashapp",
    label: "Cash App (manual)",
    sub: "Send to $deekenros directly",
    icon: <DollarSign size={18} />,
  },
  {
    id: "paypal",
    label: "PayPal",
    sub: "Coming soon",
    icon: <Apple size={18} className="rotate-12" />,
    disabled: true,
  },
];

const CheckoutModal = ({ open, onClose }) => {
  const { list, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [payment, setPayment] = useState("stripe_elements");
  const [confirmPickup, setConfirmPickup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [error, setError] = useState("");
  const [config, setConfig] = useState({ cashapp: "$deekenros", zelle: "", stripe_elements_ready: false });

  useEffect(() => {
    axios.get(`${API}/config/public`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const valid = form.name.trim() && form.phone.trim() && confirmPickup && list.length > 0 && payment !== "paypal";

  const submit = async () => {
    if (!valid || submitting) return;
    setError("");
    setSubmitting(true);
    const items = list.map(({ item, qty }) => ({ id: item.id, name: item.name, quantity: qty }));
    const base = {
      items: items.map(({ id, quantity }) => ({ id, quantity })),
      customer_name: form.name.trim(),
      customer_phone: form.phone.trim(),
      customer_email: form.email.trim(),
      notes: form.notes.trim(),
    };

    try {
      if (payment === "stripe_elements") {
        // Navigate to internal /checkout — PaymentIntent created there.
        const orderInfoForCheckout = { ...base, items: items };
        clear();
        onClose();
        navigate("/checkout", { state: { orderInfo: orderInfoForCheckout } });
        return;
      }

      const res = await axios.post(`${API}/orders/manual`, { ...base, payment_method: payment });
      setManualResult(res.data);
      clear();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAll = () => {
    setManualResult(null);
    setError("");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div {...drawerOverlay} className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" onClick={closeAll} data-testid="checkout-overlay" />
          <motion.div {...modalEnter} className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8 pointer-events-none" data-testid="checkout-modal">
            <div className="pointer-events-auto w-full max-w-2xl max-h-[92vh] overflow-y-auto" style={{ background: "var(--awd-surface)", border: "1px solid var(--awd-border)" }}>
              <div className="kente-stripe" />
              <div className="flex justify-between items-center p-6 border-b sticky top-0" style={{ borderColor: "var(--awd-border)", background: "var(--awd-surface)" }}>
                <div>
                  <div className="eyebrow">Pre-Order Checkout</div>
                  <div className="font-display text-2xl sm:text-3xl">Confirm your plate</div>
                </div>
                <button onClick={closeAll} className="p-2 hover:bg-[#231e1c]" data-testid="checkout-close"><X size={20} /></button>
              </div>

              {manualResult ? <ManualResult result={manualResult} onClose={closeAll} config={config} /> : (
                <CheckoutForm
                  list={list} subtotal={subtotal} form={form} handleChange={handleChange}
                  payment={payment} setPayment={setPayment}
                  confirmPickup={confirmPickup} setConfirmPickup={setConfirmPickup}
                  config={config}
                  valid={valid} submitting={submitting} error={error} onSubmit={submit}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const ManualResult = ({ result, onClose, config }) => {
  const isCash = result.payment_method === "cashapp";
  return (
    <div className="p-8 text-center" data-testid="manual-result">
      <CheckCircle2 size={48} className="mx-auto" style={{ color: "var(--awd-ochre)" }} />
      <div className="font-display text-3xl mt-4" style={{ color: "var(--awd-ochre)" }}>Almost there!</div>
      <p className="text-base mt-3 max-w-md mx-auto" style={{ color: "var(--awd-text-2)" }}>{result.instructions}</p>

      <div className="inline-block px-8 py-6 border mt-6" style={{ borderColor: "var(--awd-ochre)" }}>
        <div className="label-mini" style={{ color: "var(--awd-ochre)" }}>{isCash ? "Cash App" : "Zelle"}</div>
        <div className="font-display text-3xl sm:text-4xl mt-1" data-testid="manual-recipient">
          {result.recipient || (isCash ? config.cashapp : config.zelle)}
        </div>
        <div className="text-sm mt-2" style={{ color: "var(--awd-text-2)" }}>Send <strong>${result.amount?.toFixed(2)}</strong></div>
      </div>

      <div className="mt-6 p-3 text-xs inline-flex items-start gap-2 max-w-md mx-auto text-left" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b", color: "#f59e0b" }}>
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>Your order is marked <strong>Pending Confirmation</strong>. We'll text you once payment is verified, then share pickup details.</span>
      </div>

      <div className="mt-4 text-xs" style={{ color: "var(--awd-text-3)" }}>Reference: {result.order_id?.slice(0, 8)}</div>
      <button onClick={onClose} className="btn-primary mt-8">Done</button>
    </div>
  );
};

const CheckoutForm = ({ list, subtotal, form, handleChange, payment, setPayment, confirmPickup, setConfirmPickup, config, valid, submitting, error, onSubmit }) => (
  <div className="p-6 sm:p-8">
    <div className="mb-6 p-4 border" style={{ borderColor: "var(--awd-border)" }}>
      <div className="label-mini mb-2">Order Summary</div>
      {list.map(({ item, qty }) => (
        <div key={item.id} className="flex justify-between text-sm py-1">
          <span>{item.name} <span style={{ color: "var(--awd-text-3)" }}>× {qty}</span></span>
          <span style={{ color: "var(--awd-text-2)" }}>${(item.price * qty).toFixed(2)}</span>
        </div>
      ))}
      <div className="flex justify-between mt-3 pt-3 border-t" style={{ borderColor: "var(--awd-border)" }}>
        <span className="label-mini">Total</span>
        <span className="font-display text-2xl" style={{ color: "var(--awd-ochre)" }}>${subtotal.toFixed(2)}</span>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
      <Field label="Full Name *" name="name" value={form.name} onChange={handleChange} placeholder="Ama Mensah" testId="checkout-name" />
      <Field label="Phone *" name="phone" value={form.phone} onChange={handleChange} placeholder="(402) 555-0100" testId="checkout-phone" />
    </div>
    <Field label="Email (optional)" name="email" value={form.email} onChange={handleChange} placeholder="you@email.com" testId="checkout-email" />
    <div className="mt-5">
      <div className="label-mini mb-2">Notes / Allergies</div>
      <textarea className="field" name="notes" value={form.notes} onChange={handleChange} rows={2}
        placeholder="Spicy preference, allergies, etc." data-testid="checkout-notes" />
    </div>

    <div className="mt-7">
      <div className="label-mini mb-3">Payment Method</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PAY_METHODS.map((m) => (
          <button
            key={m.id} type="button"
            onClick={() => !m.disabled && setPayment(m.id)}
            disabled={m.disabled}
            className={`pay-option text-left ${payment === m.id ? "active" : ""}`}
            style={{ opacity: m.disabled ? 0.45 : 1, cursor: m.disabled ? "not-allowed" : "pointer" }}
            data-testid={`pay-${m.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3 text-sm font-semibold">
                <span style={{ color: payment === m.id ? "var(--awd-ochre)" : "var(--awd-text-2)" }}>{m.icon}</span>
                {m.label}
              </span>
              {m.badge && <span className="text-[10px] tracking-widest uppercase" style={{ color: "var(--awd-ochre)" }}>{m.badge}</span>}
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--awd-text-3)" }}>{m.sub}</div>
          </button>
        ))}
      </div>

      {payment === "stripe_elements" && !config.stripe_elements_ready && (
        <div className="mt-3 p-3 text-xs flex items-start gap-2" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b", color: "#f59e0b" }} data-testid="stripe-setup-banner">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span><strong>Setup required:</strong> Stripe keys not configured yet. The Card / Cash App Pay checkout page will load but payment confirmation needs a real Stripe key.</span>
        </div>
      )}
      {payment === "zelle" && (
        <div className="mt-3 p-3 text-sm flex items-center justify-between" style={{ background: "rgba(234,166,54,0.08)", border: "1px solid var(--awd-ochre)" }}>
          <span style={{ color: "var(--awd-text-2)" }}>Send Zelle to</span>
          <span className="font-display text-xl" style={{ color: "var(--awd-ochre)" }}>{config.zelle || "—"}</span>
        </div>
      )}
      {payment === "cashapp" && (
        <div className="mt-3 p-3 text-sm flex items-center justify-between" style={{ background: "rgba(234,166,54,0.08)", border: "1px solid var(--awd-ochre)" }}>
          <span style={{ color: "var(--awd-text-2)" }}>Send Cash App to</span>
          <span className="font-display text-xl" style={{ color: "var(--awd-ochre)" }}>{config.cashapp || "$deekenros"}</span>
        </div>
      )}
    </div>

    <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="confirm-pickup-label">
      <input type="checkbox" checked={confirmPickup} onChange={(e) => setConfirmPickup(e.target.checked)} className="mt-1" data-testid="confirm-pickup-checkbox" />
      <span className="text-sm" style={{ color: "var(--awd-text-2)" }}>
        I confirm I can pick up on <strong style={{ color: "var(--awd-text)" }}>Saturday, June 6, 12 PM – 5 PM</strong> in Omaha, NE.
      </span>
    </label>

    {error && (
      <div className="mt-4 p-3 text-sm" style={{ background: "rgba(155,34,38,0.15)", color: "#ffb4b4", border: "1px solid var(--awd-jollof)" }} data-testid="checkout-error">{error}</div>
    )}

    <button onClick={onSubmit} disabled={!valid || submitting} className="btn-primary w-full mt-6" data-testid="checkout-submit">
      {submitting ? <><Loader2 size={16} className="animate-spin" /> Processing…</> :
        payment === "stripe_elements" ? `Continue to Payment · $${subtotal.toFixed(2)}` : `Place Order · $${subtotal.toFixed(2)}`}
    </button>
  </div>
);

const Field = ({ label, name, value, onChange, placeholder, testId }) => (
  <div>
    <div className="label-mini mb-2">{label}</div>
    <input className="field" name={name} value={value} onChange={onChange} placeholder={placeholder} data-testid={testId} />
  </div>
);

export default CheckoutModal;
